import { Widget } from '../core/Widget.js';

// widgets/TripletWidget.ts
/**
 * Виджет «триплет».
 *
 * Разметка (два варианта):
 *   1) True / False
 *     <input type="hidden" data-key="..." data-widget="triplet" value="" autocomplete="off">
 *     <div class="triplet">
 *       <button type="button" value="True">Да</button>
 *       <button type="button" value="False">Нет</button>
 *     </div>
 *
 * Двухкнопочный вариант уже дает 3 состояния данных:
 *   True / False / null
 * потому что повторный клик по активной кнопке очищает hidden value.
 *
 *   2) True / 0 / False
 *     <input type="hidden" data-key="..." data-widget="triplet" value="" autocomplete="off">
 *     <div class="triplet">
 *       <button type="button" value="True">Да</button>
 *       <button type="button" value="0" class="zero">0</button>
 *       <button type="button" value="False">Нет</button>
 *     </div>
 *
 * Трехкнопочный вариант дает уже 4 состояния данных:
 *   True / False / 0 / null
 * потому что "0" живет отдельно от пустого значения.
 *
 * Правила:
 *  • Хост — это input[type=hidden] (доступен как this.element).
 *  • Панель — соседний div.triplet (обычно сразу после input).
 *  • Клик по кнопке:
 *      – снимает .active со всех кнопок панели,
 *      – повторный клик по активной очищает значение (input.value = ''),
 *      – иначе применяет значение из button[value],
 *      – диспатчит 'change' на input (совместимость со слушателями).
 *  • Пустое значение в input трактуется как null в getValue(),
 *    чтобы encodeValues положил в результат "dbnull".
 *  • setValue не диспатчит событий (как и другие setValue), только синхронизирует UI.
 *  • Если input.disabled — обработчики кликов не вешаются.
 */
class TripletWidget extends Widget {
    constructor() {
        super(...arguments);
        /** Ссылка на панель кнопок (div.triplet). Может отсутствовать, если разметка неполная. */
        this.panel = null;
        /** Список «диспозеров» для снятия обработчиков при уничтожении виджета. */
        this._disposers = [];
    }
    /** Ключевой класс для поиска виджета */
    static _cssKey() {
        return "im-triplet";
    }
    /**
     * Инициализация виджета.
     * • Находит панель (div.triplet) рядом с input (this.element).
     * • Синхронизирует .active на кнопках с текущим value.
     * • Вешает обработчики кликов (если input не disabled).
     */
    _create(_op) {
        // Ищем соседнюю панель с кнопками
        this.panel = this.findPanel(this.element);
        // Инициализируем визуальное состояние по текущему value
        this.syncButtonsFromValue();
        // Если disabled — не вешаем клики
        const disabled = !!this.element.disabled || this.element.hasAttribute('disabled');
        if (!disabled && this.panel) {
            this.bindClicks(this.panel, this.element);
        }
    }
    /**
     * Завершение работы виджета.
     * • Снимает все навешанные обработчики.
     * • Обнуляет ссылку на панель для высвобождения памяти.
     */
    _onDestroy() {
        for (const off of this._disposers) {
            try {
                off();
            }
            catch { /* no-op */ }
        }
        this._disposers = [];
        this.panel = null;
    }
    // ===== Реализация WithMainValue =====
    /**
     * Текущее значение.
     * • ''  → null
     * • 'true'/'True'/'+'  → true
     * • 'false'/'False'/'-' → false
     * • числовая строка (в т.ч. '0') → number
     */
    getValue() {
        const raw = this.element.value ?? '';
        if (raw === '')
            return null;
        const s = raw.trim();
        const low = s.toLowerCase();
        if (low === 'true')
            return true;
        if (low === 'false')
            return false;
        const n = Number(s);
        if (Number.isFinite(n))
            return n;
        // на всякий случай: неизвестные строки считаем отсутствием значения
        return null;
    }
    /**
     * Установить значение программно (без событий) и синхронизировать .active.
     * • null/undefined → очистка
     * • boolean → 'True' / 'False'
     * • number  → строка числа (напр. 0 → '0')
     * • string  → как есть
     */
    setValue(val) {
        let s = '';
        if (val == null) {
            s = '';
        }
        else if (typeof val === 'boolean') {
            s = val ? 'True' : 'False';
        }
        else if (typeof val === 'number') {
            s = String(val);
        }
        else {
            s = String(val);
        }
        this.element.value = s;
        this.syncButtonsFromValue();
        return true;
    }
    /**
     * Проверка «задано ли значение».
     * • true — если input.value непустой,
     * • false — если пустой (очищено).
     */
    hasValue() {
        return (this.element.value ?? '') !== '';
    }
    // ===== Внутренние методы =====
    /**
     * Поиск панели (div.triplet), связанной с input:
     * 1) Прямой следующий сосед.
     * 2) Fallback: ближайший прямой потомок .triplet у общего родителя.
     */
    findPanel(input) {
        // 1) прямой следующий элемент
        const sib = input.nextElementSibling;
        if (sib && sib instanceof HTMLElement && sib.classList.contains('triplet')) {
            return sib;
        }
        // 2) fallback: ближайший прямой потомок .triplet у родителя
        const parent = input.parentElement;
        if (parent) {
            const p = parent.querySelector(':scope > .triplet');
            if (p)
                return p;
        }
        return null;
    }
    /**
     * Навешивание обработчиков кликов на кнопки.
     * • Сохраняем «диспозеры» в _disposers, чтобы корректно снимать обработчики в _onDestroy().
     * • По клику синхронизируем input.value и класс .active у кнопок.
     * • Диспатчим 'change' на input (совместимость со сторонними слушателями).
     */
    bindClicks(panel, input) {
        panel.querySelectorAll('button').forEach(btn => {
            const handler = () => {
                // важно: поддерживаем включение disabled ПОСЛЕ инициализации виджета
                if (input.disabled || input.hasAttribute('disabled'))
                    return;
                const wasActive = btn.classList.contains('active');
                // снять актив со всех
                panel.querySelectorAll('button.active').forEach(b => b.classList.remove('active'));
                if (wasActive) {
                    input.value = '';
                }
                else {
                    btn.classList.add('active');
                    input.value = btn.getAttribute('value') ?? '';
                }
                input.dispatchEvent(new Event('change', { bubbles: true }));
            };
            btn.addEventListener('click', handler);
            this._disposers.push(() => btn.removeEventListener('click', handler));
        });
    }
    /**
     * Синхронизация UI из текущего input.value.
     * • Выставляет/снимает .active у кнопок панели в соответствии со значением.
     * • Пустое значение снимает .active у всех.
     */
    syncButtonsFromValue() {
        if (!this.panel)
            return;
        const v = this.element.value ?? '';
        this.panel.querySelectorAll('button').forEach(b => {
            const match = (b.getAttribute('value') ?? '') === v && v !== '';
            b.classList.toggle('active', match);
        });
    }
    /** Получение названия значения исходя из разметки или контекста */
    /** Название текущего значения — только из кнопки в панели */
    getValueTitle() {
        const v = this.getValue();
        if (v == null)
            return null;
        // какое значение искать в DOM-кнопках
        const domValue = typeof v === "boolean" ? (v ? "True" : "False") : String(v);
        const panel = this.panel ?? this.findPanel(this.element);
        if (!panel)
            return null;
        const btn = panel.querySelector(`button[value="${CSS.escape(domValue)}"]`);
        if (!btn)
            return v.toString();
        const text = (btn.textContent ?? "").trim();
        return text ?? v;
    }
}
/** Имя виджета */
TripletWidget._alias = "Triplet";

export { TripletWidget };
